import React, { useState, useMemo, useContext, useEffect, memo } from "react";
import { Autocomplete, TextField, Grid, IconButton, Typography} from "@mui/material";
import { ExpandMore, ChevronRight } from "@mui/icons-material";
import style from "../../../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss"
import workflowStyle from '../../../../../assets/styles/componentStyles/manychatStyles/workflowFormComponents/workflowGlobal.module.scss';
import globalStyles from "../../../../../assets/styles/global.module.scss";
import { intent, intentConfigurationPayload } from "../../../../../models/axiosModel";
import { FlowBuilderContext, FlowBuilderContextPayload } from "../../../../../context/flowBuilderContext";
import { isCurrentViewVersionPublished } from "../../utilityFunctions";
import { WorkflowContext, WorkflowContextPayload } from "../../../../../context/workflowContext";
import { getDefaultValueOfIntentConfigurationPayload } from "../../flowBuilderConstants";
import { useIntentStore } from "../../../../../store/intentStore";

// interface Intent {
//     intent_id: string;
//     intent_name: string;
//     child_intent?: Intent[];
// }

interface FlattenedIntent extends intent{
    id: string;
    name: string;
    level: number;
    parentId?: string;
    isParent: boolean;
    parentChain: string[];
}

interface Props {
    payload: intentConfigurationPayload, 
    setPayload: React.Dispatch<React.SetStateAction<intentConfigurationPayload>>,
    selectedIntent: intentConfigurationPayload | null,  
    setFlagSameIntentExist: React.Dispatch<React.SetStateAction<boolean>>,
}

const HierarchicalIntentAutocomplete: React.FC<Props> = ({ payload, setPayload, selectedIntent, setFlagSameIntentExist }) => {

    const intentDataList = useIntentStore((store) => store.intentDataList);

    const { currentActiveStaticWorkflow} = useContext(WorkflowContext) as WorkflowContextPayload;

    const sortIntents = (intent: intent[]) => {
        return intent.sort((a,b) => {
            return new Date(a.created_at_timestamp).getTime() - new Date(b.created_at_timestamp).getTime();
        })
    }

    const flattenIntents = (items: intent[], level = 0, parentChain: string[] = []): FlattenedIntent[] => {
        return sortIntents(items).flatMap((intent) => {
        const isParent = Boolean(intent.child_intent && intent.child_intent.length > 0);
        const current: FlattenedIntent = {
            id: intent.intent_id,
            name: intent.intent_name,
            level,
            parentId: parentChain[parentChain.length - 1],
            isParent,
            parentChain,
            ...intent,
        };
        const children = intent.child_intent
            ? flattenIntents(intent.child_intent, level + 1, [...parentChain, intent.intent_id])
            : [];
        return [current, ...children];
        });
    };

    const allIntents = useMemo(() => flattenIntents(intentDataList ?? []), [intentDataList]);

    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const toggleExpand = (id: string) => {
            setExpanded((prev) => {
            const newSet = new Set(prev);
            newSet.has(id) ? newSet.delete(id) : newSet.add(id);
            return newSet;
        });
    };

    // const visibleIntents = useMemo(() => {
    //     const visible: FlattenedIntent[] = [];
    //     const expandedSet = new Set(expanded);

    //     for (const intent of allIntents) {
    //         const show =
    //             intent.level === 0 ||
    //             intent.parentChain.every((ancestorId) => expandedSet.has(ancestorId));
    //         if (show) visible.push(intent);
    //     }
    //     return visible;
    // }, [expanded, allIntents]);

    const valueOfAutoComplete = (intent_id: string | null) => {
        return allIntents?.find((int) => int?.id == intent_id) ?? null;
    }

    const onChangeOfIntent = (val: FlattenedIntent | null) => {
        setPayload((prev) => ({
            ...prev, 
            intent_id: val?.id ?? null,
            intent_name: val?.name ?? "",
        })); 
        setFlagSameIntentExist(false);
    }

    useEffect(() => {
        if (selectedIntent?.intent_id) {
            const found = allIntents.find((i) => i.id === selectedIntent?.intent_id);
            if (found && found.parentChain.length > 0) {
                setExpanded((prev) => new Set([...Array.from(prev), ...found.parentChain]));
            }
        }
    }, [selectedIntent, allIntents]); 

    const checkDisableConfiguredIntent = (intent: FlattenedIntent) => {
        const child_intents = intent?.child_intent; 

        if(child_intents?.length == 0){
            if(intent?.flag_remaining){
                return true;
            } else  {
                return false;
            }
        }

        for(let i = 0; i < child_intents?.length; ++i){
            if(child_intents[i]?.flag_remaining){
                return false;
            }
        }
        return true;
    }

    const renderIntentOptions = (props: React.HTMLAttributes<HTMLLIElement>, option: FlattenedIntent) => {
        const isExpanded = expanded.has(option.id);
        const indent = 25 * option.level;

        const isVisible = option.level === 0 || option.parentChain.every((id) => expanded.has(id));
        if (!isVisible) return null;

        return (
            <li 
                {...props} 
                key={option.id} 
                className={`${props.className} ${workflowStyle.intentConfigurationDropdown}`}
            >
                <Grid container className={`${globalStyles.alignItemCenter} `} sx={{ pl: `${indent}px` }}>
                    {(!option.isParent && option.child_intent?.length != 0) && (
                        <div className={workflowStyle.indentIntentDiv} /> // spacing for non-parent items
                    )}
                    <Typography className={`${workflowStyle.paddingLeftIntentOption} ${checkDisableConfiguredIntent(option) ? workflowStyle.intentOptionLowOpacity : workflowStyle.intentConfigurationDropdown}`}>{option.name}</Typography>
                    {option.isParent && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(option.id);
                            }}
                            className={`${workflowStyle.toggleIconButtonInIntentDropdown}`}
                        >
                            {isExpanded ? <ExpandMore /> : <ChevronRight />}
                        </IconButton>
                    )}
                </Grid>
            </li>
        );
    }

    const onChangeOfFilter = (options: FlattenedIntent[], inputValue: string) => {
        if (!inputValue) {
            // Return all the Intent's If no search value found.
            return options.filter((opt) => (opt.level === 0 || opt.parentChain.every((id) => expanded.has(id))));
        }

        // When Search something Returns 
        // 1. Intents matching search
        // 2. All Parent's of those intent.

        const searchedValue = inputValue.toLowerCase();
        const matched = options.filter((opt) => opt.name.toLowerCase().includes(searchedValue));

        const parentIdsToInclude = new Set<string>();
        
        matched.forEach((matchedVal) => {matchedVal.parentChain.forEach((parent) => parentIdsToInclude.add(parent));});
        
        return options.filter((opt) => matched.includes(opt) || parentIdsToInclude.has(opt.id));
    }

    return (
            <Autocomplete
                options={allIntents} // visibleIntents
                value={valueOfAutoComplete(payload?.intent_id ?? null)}
                onChange={(_, val) => onChangeOfIntent(val)}
                className={`${style.contactFilterDropdownsInPopup}`} 
                getOptionLabel={(option) => option.name}
                renderInput={(params) => <TextField {...params} placeholder="Select Intent" />}
                getOptionDisabled={(option) => checkDisableConfiguredIntent(option)}
                renderOption={(props, option) => renderIntentOptions(props, option)} 
                filterOptions={(options, { inputValue }) => onChangeOfFilter(options, inputValue)}
                isOptionEqualToValue={(opt, val) => opt.id === val.id} 
                disabled={isCurrentViewVersionPublished(currentActiveStaticWorkflow)}
            />
    );
};

export default memo(HierarchicalIntentAutocomplete, (prev, next) => {
    if(prev?.payload?.intent_id !== next?.payload?.intent_id || prev?.payload?.intent_name !== next?.payload?.intent_name){
        return false;
    }

    return true;
})